import type React from 'react';
import { useMemo } from 'react';

import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF';
import { faLinkedinIn } from '@fortawesome/free-brands-svg-icons/faLinkedinIn';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons/faXTwitter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import * as ContextMenu from '@radix-ui/react-context-menu';

import { Analytics } from '@/Telemetry';
import { useAppStore } from '@/inertia/AppStore';
import { FrontendEmailSharing } from './EmailSharing/EmailSharing';
import QrCodeSharing from './QrCodeSharing';

import styles from './Share.module.css';
import buttonStyles from './button.module.css';

const config = [
  { name: 'facebook', icon: <FontAwesomeIcon icon={faFacebookF} /> },
  { name: 'twitter', icon: <FontAwesomeIcon icon={faXTwitter} /> },
  { name: 'linkedin', icon: <FontAwesomeIcon icon={faLinkedinIn} /> },
];

const onShare = (name: string, generateRid = false) => {
  const url = new URL(window.location.href);

  url.searchParams.set('utm_source', 'zn_share_btn');
  url.searchParams.set('utm_medium', 'share_btn');
  url.searchParams.set('utm_campaign', 'share_btn');

  // random id is preserved in localstate
  const key = `zngly/share/rid:${encodeURIComponent(window.location.href)}`;
  let randomId = window.localStorage.getItem(key);

  if (generateRid || !randomId)
    // set random id to generate unique url
    randomId = crypto.randomUUID();
  window.localStorage.setItem(key, encodeURIComponent(randomId));

  if (!!randomId) url.searchParams.set('rid', randomId);

  let newWin = null;
  const shareUrl = encodeURIComponent(url.href);
  switch (name) {
    case 'facebook':
      newWin = window.open(`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`, '_blank');
      break;
    case 'twitter':
      newWin = window.open(`https://twitter.com/share?url=${shareUrl}`, '_blank');
      break;
    case 'linkedin':
      newWin = window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${shareUrl}`, '_blank');
      break;
    default:
      return;
  }

  Analytics.push('share', {
    eventaction: 'share',
    eventlabel: `${name} - ${url.toString()}`,
  });

  newWin?.focus();
};

type Props = {
  isFallback?: boolean;
  disabled?: boolean;
  shareTextProps?: React.HTMLAttributes<HTMLDivElement>;
};

export const ShareContainer = (props: Props) => {
  const isAppPreview = useAppStore((state) => state.isAppPreview);

  const isDisabled = useMemo(() => isAppPreview || props?.disabled || props?.isFallback, [isAppPreview, props]);

  return (
    <div className={styles.container}>
      <div className={styles.shareText} {...props?.shareTextProps}>
        <p>Share</p>
      </div>
      {config.map(({ name, icon }) => (
        <ContextMenu.Root key={name}>
          <ContextMenu.Trigger className="ContextMenuTrigger" asChild disabled={props?.isFallback}>
            <button
              type="button"
              className={buttonStyles.shareBtn}
              onClick={() => onShare(name)}
              aria-label={`Share to ${name}`}
              disabled={isDisabled}
            >
              {icon}
            </button>
          </ContextMenu.Trigger>
          <ContextMenu.Portal>
            <ContextMenu.Content className={styles.ContextMenuContent}>
              <ContextMenu.Item
                className={styles.ContextMenuItem}
                onSelect={() => onShare(name, true)}
                disabled={isDisabled}
              >
                Generate New Preview
              </ContextMenu.Item>
            </ContextMenu.Content>
          </ContextMenu.Portal>
        </ContextMenu.Root>
      ))}
      <FrontendEmailSharing isFallback={props?.isFallback} buttonClassName={buttonStyles.shareBtn} />
      <QrCodeSharing disabled={isDisabled} isFallback={props?.isFallback} buttonClassName={buttonStyles.shareBtn} />
    </div>
  );
};

export default ShareContainer;
